
import React, { useEffect, useState } from "react";
import { ArrowRight, Building, Shield, Globe, Users, BarChart, Clock, Check, DollarSign, Briefcase, BadgeCheck, Cpu, Network, TrendingUp, Target, Trophy, Sparkles, Zap, CheckCircle, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProductDemoSection } from "@/components/landing/ProductDemoSection";
import { Badge } from "@/components/ui/badge";

const Enterprise = () => {
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  // Dynamic background movement based on mouse position
  const calculateOffset = (factor: number = 0.02) => {
    const { x, y } = mousePosition;
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    
    return {
      x: (x - centerX) * factor,
      y: (y - centerY) * factor
    };
  };

  const offset = calculateOffset();

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white">
      {/* Hero Section */}
      <section className="pt-28 md:pt-36 lg:pt-40 pb-20 relative overflow-hidden">
        {/* Enhanced background gradients */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_1000px_at_100%_200px,rgba(14,165,233,0.08),transparent)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_0%_400px,rgba(16,185,129,0.09),transparent)]" />
        <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(16,185,129,0.03)_0%,rgba(14,165,233,0.03)_100%)]" />
        <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-white to-transparent" />
        
        {/* Animated particles */}
        <div className="absolute top-20 right-20 w-4 h-4 rounded-full bg-sky-300/20 animate-pulse" style={{animationDuration: '3s'}} />
        <div className="absolute top-40 left-32 w-6 h-6 rounded-full bg-emerald-300/20 animate-pulse" style={{animationDuration: '4s'}} />
        <div className="absolute bottom-32 right-32 w-5 h-5 rounded-full bg-sky-300/20 animate-pulse" style={{animationDuration: '5s'}} />
        
        {/* Advanced interactive backgrounds - parallax effect */}
        <div 
          className="absolute inset-0 bg-[radial-gradient(circle_1500px_at_50%_-30%,rgba(14,165,233,0.15),transparent)]" 
          style={{ transform: `translateY(${scrollY * 0.1}px)` }}
        />
        <div 
          className="absolute inset-0 bg-[radial-gradient(circle_1200px_at_80%_20%,rgba(16,185,129,0.12),transparent)]" 
          style={{ transform: `translateY(${scrollY * 0.05}px)` }}
        />
        <div 
          className="absolute inset-0 bg-[radial-gradient(circle_900px_at_20%_60%,rgba(56,189,248,0.08),transparent)]" 
          style={{ transform: `translateY(${scrollY * 0.07}px)` }}
        />
        
        {/* Light beam effect */}
        <div className="absolute top-0 left-1/2 w-[1000px] h-[600px] -translate-x-1/2 bg-gradient-to-b from-sky-400/5 via-sky-300/5 to-transparent rotate-12 transform-gpu blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r from-emerald-50 to-emerald-100 text-emerald-700 ring-1 ring-emerald-200/70 shadow-sm mb-4 animate-fade-in">
              <Building className="w-4 h-4 mr-2 text-emerald-500" />
              <span>Enterprise Freelancing Platform</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
              <span className="block">Transform Your Enterprise</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-emerald-600">with Flexible Talent</span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Access top-tier freelancers and agencies with enterprise-grade security, compliance, 
              and management tools designed for large organizations.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-8 py-6 h-auto group relative overflow-hidden shadow-lg hover:shadow-emerald-500/25 transition-all duration-300">
                  <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-emerald-400 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <span className="relative flex items-center">
                    Schedule a Consultation
                    <ArrowRight className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </Button>
              </Link>
              <Link to="/auth">
                <Button size="lg" variant="outline" className="border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 px-8 py-6 h-auto group hover:border-emerald-300 transition-all duration-200">
                  <Play className="mr-2 h-5 w-5 text-emerald-500 group-hover:text-emerald-600" />
                  <span>Try Demo</span>
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Featured clients with enhanced styling */}
          <div className="mt-12 md:mt-20">
            <p className="text-center text-sm uppercase tracking-wider text-gray-500 font-medium mb-6">
              Trusted by leading enterprises worldwide
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
              {['Microsoft', 'Google', 'Amazon', 'IBM', 'Oracle'].map((company, index) => (
                <div 
                  key={company} 
                  className="text-xl font-bold text-gray-400 hover:text-gray-600 transition-colors duration-300"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  {company}
                </div>
              ))}
            </div>
            
            {/* Social proof metrics */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {[
                { value: "97%", label: "Client Satisfaction" },
                { value: "10K+", label: "Verified Experts" },
                { value: "$250M+", label: "Project Value" },
                { value: "120+", label: "Countries Served" }
              ].map((stat, index) => (
                <div 
                  key={index} 
                  className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
                >
                  <div className="text-2xl font-bold text-sky-600">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Professional Solutions Section - Imported from landing page */}
      <ProductDemoSection />
      
      {/* Professional Solutions Section - Enhanced Enterprise Solutions */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_900px_at_50%_-30%,rgba(14,165,233,0.12),transparent)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_1200px_at_70%_60%,rgba(16,185,129,0.08),transparent)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_20%_40%,rgba(125,211,252,0.10),transparent)]" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Badge variant="outline" className="px-4 py-2 rounded-full border-sky-200 bg-sky-50/50 text-sky-700 mb-4">
              <Briefcase className="w-4 h-4 mr-2 text-sky-500" />
              <span>Enterprise-Grade Solutions</span>
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Professional Solutions for Business Growth
            </h2>
            <p className="text-lg text-gray-600">
              Our comprehensive suite of enterprise tools delivers measurable results through innovative technology 
              and proven methodologies that drive business transformation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: <Shield className="w-10 h-10 text-sky-600" />,
                title: "Enterprise Security",
                description: "Multi-layered security architecture with SOC 2 compliance and end-to-end encryption for your sensitive data.",
                metric: "99.99%",
                metricLabel: "uptime SLA"
              },
              {
                icon: <TrendingUp className="w-10 h-10 text-sky-600" />,
                title: "Business Intelligence",
                description: "Advanced analytics and reporting tools that provide actionable insights for strategic decision making.",
                metric: "35%",
                metricLabel: "efficiency gain"
              },
              {
                icon: <Network className="w-10 h-10 text-sky-600" />,
                title: "Seamless Integration",
                description: "Connect with your existing enterprise systems through our comprehensive API and integration suite.",
                metric: "100+",
                metricLabel: "integrations"
              },
              {
                icon: <Users className="w-10 h-10 text-sky-600" />,
                title: "Talent Ecosystem",
                description: "Access to a network of pre-vetted professionals with enterprise experience across industries.",
                metric: "10K+",
                metricLabel: "verified experts"
              },
              {
                icon: <Target className="w-10 h-10 text-sky-600" />,
                title: "Strategic Resource Planning",
                description: "AI-powered resource allocation and project planning tools tailored for enterprise needs.",
                metric: "45%",
                metricLabel: "faster delivery"
              },
              {
                icon: <Globe className="w-10 h-10 text-sky-600" />,
                title: "Global Compliance",
                description: "Built-in compliance mechanisms for international operations and regulatory requirements.",
                metric: "120+",
                metricLabel: "countries supported"
              }
            ].map((solution, index) => (
              <Card 
                key={index} 
                className="border-none shadow-lg hover:shadow-xl transition-all duration-300 group hover:-translate-y-1 overflow-hidden"
              >
                <CardContent className="p-8 relative">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-sky-50/50 rounded-full -mr-20 -mt-20 group-hover:bg-sky-100/50 transition-colors duration-300" />
                  
                  <div className="flex justify-between items-start mb-6 relative z-10">
                    <div className="p-3 rounded-lg bg-sky-50 inline-block group-hover:bg-sky-100 group-hover:scale-110 transition-all duration-300">
                      {solution.icon}
                    </div>
                    <div className="bg-sky-50 px-3 py-1 rounded-full group-hover:bg-sky-100 transition-colors duration-300">
                      <span className="text-xl font-bold text-sky-700">{solution.metric}</span>
                      <span className="text-sm text-sky-600 ml-1">{solution.metricLabel}</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 relative z-10">{solution.title}</h3>
                  <p className="text-gray-600 mb-4 relative z-10">{solution.description}</p>
                  <div className="pt-2 relative z-10">
                    <Link to="/contact" className="text-sky-600 font-medium flex items-center hover:text-sky-700 transition-colors group">
                      Learn more 
                      <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Solution showcase with tabs - Enhanced styling */}
          <div className="mt-20 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 md:p-10 shadow-lg border border-gray-100">
            <div className="text-center mb-10">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                Enterprise Solutions in Action
              </h3>
              <p className="text-gray-600 max-w-2xl mx-auto">
                See how our solutions deliver measurable business outcomes across different enterprise functions
              </p>
            </div>
            
            <Tabs defaultValue="operations" className="w-full">
              <TabsList className="w-full flex flex-wrap justify-center gap-2 mb-8 bg-transparent">
                {[
                  { id: "operations", label: "Operations", icon: <Cpu className="w-4 h-4 mr-2" /> },
                  { id: "talent", label: "Talent Management", icon: <Users className="w-4 h-4 mr-2" /> },
                  { id: "compliance", label: "Compliance", icon: <BadgeCheck className="w-4 h-4 mr-2" /> },
                  { id: "analytics", label: "Analytics", icon: <BarChart className="w-4 h-4 mr-2" /> },
                ].map((tab) => (
                  <TabsTrigger 
                    key={tab.id} 
                    value={tab.id}
                    className="px-6 py-3 rounded-lg flex items-center gap-1 bg-white shadow-sm data-[state=active]:bg-gradient-to-r data-[state=active]:from-sky-600 data-[state=active]:to-sky-500 data-[state=active]:text-white transition-all duration-300"
                  >
                    {tab.icon}
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              {[
                {
                  id: "operations",
                  title: "Operational Excellence",
                  description: "Streamline operations and reduce overhead costs with intelligent workflow automation",
                  features: ["Workflow automation", "Resource optimization", "Cross-functional collaboration", "Project lifecycle management"],
                  stats: [{ label: "Efficiency Gain", value: "37%" }, { label: "Cost Reduction", value: "25%" }],
                  image: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80"
                },
                {
                  id: "talent",
                  title: "Talent Optimization",
                  description: "Access top-tier talent on demand and manage your external workforce with powerful tools",
                  features: ["Verified talent pool", "Skill-based matching", "Team collaboration", "Performance tracking"],
                  stats: [{ label: "Time-to-hire", value: "-65%" }, { label: "Talent quality", value: "+42%" }],
                  image: "https://images.unsplash.com/photo-1552581234-26160f608093?auto=format&fit=crop&w=800&q=80"
                },
                {
                  id: "compliance",
                  title: "Compliance Management",
                  description: "Ensure regulatory compliance and mitigate risk with built-in compliance tools",
                  features: ["Cross-border regulations", "Audit trails", "Contract management", "Compliance reporting"],
                  stats: [{ label: "Compliance Rate", value: "99.8%" }, { label: "Audit time", value: "-70%" }],
                  image: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=800&q=80"
                },
                {
                  id: "analytics",
                  title: "Enterprise Analytics",
                  description: "Make data-driven decisions with comprehensive analytics and business intelligence",
                  features: ["Real-time dashboards", "Predictive analytics", "Performance metrics", "Custom reports"],
                  stats: [{ label: "Data Insights", value: "+87%" }, { label: "Decision time", value: "-50%" }],
                  image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80"
                }
              ].map((content) => (
                <TabsContent key={content.id} value={content.id}>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                    <div className="p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                      <h4 className="text-2xl font-bold text-gray-900 mb-4">{content.title}</h4>
                      <p className="text-gray-600 mb-6">{content.description}</p>
                      
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        {content.stats.map((stat, idx) => (
                          <div key={idx} className="bg-gradient-to-br from-sky-50 to-white p-4 rounded-lg shadow-sm border border-sky-100">
                            <div className="text-2xl font-bold text-sky-600">{stat.value}</div>
                            <div className="text-sm text-gray-500">{stat.label}</div>
                          </div>
                        ))}
                      </div>
                      
                      <h5 className="text-lg font-semibold text-gray-800 mb-3">Key Features</h5>
                      <ul className="space-y-2 mb-6">
                        {content.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start group">
                            <div className="rounded-full bg-emerald-50 p-1 mr-2 flex-shrink-0 mt-0.5 group-hover:bg-emerald-100 transition-colors duration-200">
                              <CheckCircle className="w-4 h-4 text-emerald-500" />
                            </div>
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <Button className="bg-gradient-to-r from-sky-600 to-sky-500 hover:from-sky-700 hover:to-sky-600 group shadow-md hover:shadow-sky-200/40 transition-all duration-300">
                        Request a Demo
                        <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                      </Button>
                    </div>
                    <div className="rounded-xl overflow-hidden shadow-lg h-80 group relative">
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center p-6">
                        <Button variant="outline" className="bg-white/90 backdrop-blur-sm">
                          View Details
                        </Button>
                      </div>
                      <img 
                        src={content.image}
                        alt={content.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      </section>
      
      {/* Key Benefits Section - Enhanced with visual elements */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
        
        {/* Decorative elements */}
        <div className="absolute right-10 top-10 w-64 h-64 bg-emerald-50 rounded-full opacity-70 blur-3xl" />
        <div className="absolute left-10 bottom-10 w-64 h-64 bg-sky-50 rounded-full opacity-70 blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r from-emerald-50 to-sky-50 text-sky-700 ring-1 ring-sky-100 shadow-sm mb-4">
              <Shield className="w-4 h-4 mr-2 text-sky-500" />
              <span>Enterprise-Grade Protection</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Enterprise-Grade Freelancing Solutions
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our platform combines the flexibility of freelance talent with the security, 
              compliance, and control that enterprises require.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Shield className="w-8 h-8 text-emerald-500" />,
                title: "Enterprise Security",
                description: "SOC 2 compliance, encryption at rest and in transit, and advanced security controls."
              },
              {
                icon: <Users className="w-8 h-8 text-sky-500" />,
                title: "Talent Vetting",
                description: "Rigorous background checks, skill verification, and NDAs for all freelancers."
              },
              {
                icon: <Globe className="w-8 h-8 text-emerald-500" />,
                title: "Global Compliance",
                description: "Built-in compliance with international labor and tax regulations."
              },
              {
                icon: <BarChart className="w-8 h-8 text-sky-500" />,
                title: "Advanced Analytics",
                description: "Real-time reporting, spend analysis, and ROI tracking for all projects."
              },
              {
                icon: <Clock className="w-8 h-8 text-emerald-500" />,
                title: "Time Tracking",
                description: "Automated time tracking with screenshot verification and activity monitoring."
              },
              {
                icon: <DollarSign className="w-8 h-8 text-sky-500" />,
                title: "Cost Savings",
                description: "Reduce hiring costs by up to 50% compared to traditional staffing."
              }
            ].map((benefit, index) => (
              <div key={index} className="group p-6 border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-all bg-white hover:-translate-y-1 duration-300 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <div className="p-3 rounded-lg bg-gray-50 inline-block mb-4 group-hover:bg-gradient-to-br group-hover:from-sky-50 group-hover:to-emerald-50 transition-all duration-300 relative z-10">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 relative z-10">{benefit.title}</h3>
                <p className="text-gray-600 relative z-10">{benefit.description}</p>
                
                <div className="absolute bottom-0 right-0 w-20 h-20 rounded-tl-full bg-gradient-to-br from-sky-50/50 to-emerald-50/50 opacity-0 group-hover:opacity-100 transition-all duration-300" />
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* How It Works - Enhanced with step numbers and visuals */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        {/* Advanced layered backgrounds with 3D parallax effect */}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-50/80 via-white/60 to-sky-50/20"></div>
        
        {/* Dynamic mesh gradients */}
        <div 
          className="absolute inset-0 bg-[radial-gradient(circle_1200px_at_50%_50%,rgba(56,189,248,0.08),transparent_80%)]" 
          style={{ 
            transform: `translate(${offset.x * -1}px, ${offset.y * -1}px)`,
            transition: 'transform 0.5s cubic-bezier(0.33, 1, 0.68, 1)'
          }}
        />
        
        <div 
          className="absolute inset-0 bg-[radial-gradient(circle_800px_at_80%_20%,rgba(16,185,129,0.07),transparent_70%)]"
          style={{ 
            transform: `translate(${offset.x * -0.7}px, ${offset.y * -0.7}px)`,
            transition: 'transform 0.7s cubic-bezier(0.33, 1, 0.68, 1)'
          }}
        />
        
        <div 
          className="absolute inset-0 bg-[radial-gradient(circle_600px_at_20%_80%,rgba(14,165,233,0.05),transparent_70%)]"
          style={{ 
            transform: `translate(${offset.x * -1.2}px, ${offset.y * -1.2}px)`,
            transition: 'transform 0.3s cubic-bezier(0.33, 1, 0.68, 1)'
          }}
        />
        
        {/* Animated grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(226,232,240,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(226,232,240,0.1)_1px,transparent_1px)] bg-[size:32px_32px] [mask-image:radial-gradient(ellipse_80%_80%_at_50%_50%,black,transparent)]" style={{ 
          transform: `translate3d(0, ${scrollY * 0.05}px, 0) scale(${1 + scrollY * 0.0001})` 
        }}></div>
        
        {/* Floating particles */}
        <div className="absolute h-full w-full overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <div 
              key={i}
              className="absolute rounded-full mix-blend-screen animate-float" 
              style={{
                width: `${Math.random() * 6 + 2}px`,
                height: `${Math.random() * 6 + 2}px`,
                background: `rgba(${Math.random() * 100 + 130}, ${Math.random() * 100 + 130}, 255, ${Math.random() * 0.3 + 0.2})`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDuration: `${Math.random() * 10 + 10}s`,
                animationDelay: `${Math.random() * 5}s`
              }}
            />
          ))}
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <Badge className="px-4 py-1.5 mb-4 bg-gradient-to-r from-sky-50 to-sky-100 text-sky-700 border-sky-200">
              <Zap className="w-4 h-4 mr-2" />
              Simple Process
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How Our Platform Works
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A streamlined process designed for enterprise needs from talent onboarding to project management.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            {[
              {
                number: "01",
                title: "Custom Talent Sourcing",
                description: "Our team works with you to understand your needs and sources the perfect freelancers from our vetted talent pool."
              },
              {
                number: "02",
                title: "Security & Compliance",
                description: "All freelancers go through enterprise-grade security checks and sign your required legal agreements."
              },
              {
                number: "03",
                title: "Project Management",
                description: "Utilize our advanced project management tools or integrate with your existing systems via our API."
              },
              {
                number: "04",
                title: "Ongoing Support",
                description: "Dedicated account management and 24/7 technical support throughout the entire engagement lifecycle."
              }
            ].map((step, index) => (
              <div key={index} className="flex gap-8 mb-12 items-start group">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-sky-50 to-sky-100 flex items-center justify-center border border-sky-200 group-hover:from-sky-100 group-hover:to-sky-200 transition-colors duration-300">
                    <span className="text-xl font-bold text-sky-700">{step.number}</span>
                  </div>
                </div>
                <div className="flex-grow bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </div>
            ))}
            
            <div className="text-center mt-16">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-sky-600 to-emerald-600 hover:from-sky-700 hover:to-emerald-700 text-white px-8 py-6 h-auto shadow-lg hover:shadow-emerald-500/25 transition-all duration-300"
              >
                <span className="mr-2">Start Your Enterprise Journey</span>
                <ArrowRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-sky-50 via-white to-emerald-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_100%_20%,rgba(56,189,248,0.12),transparent)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_0%_80%,rgba(16,185,129,0.12),transparent)]" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="px-4 py-1.5 mb-4 bg-gradient-to-r from-emerald-50 to-emerald-100 text-emerald-700 border-emerald-200">
              <Sparkles className="w-4 h-4 mr-2 text-emerald-500" />
              Enterprise Advantage
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Ready to Transform Your Enterprise Talent Strategy?
            </h2>
            <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
              Join leading enterprises that have streamlined operations, reduced costs, and accessed top-tier talent through our platform.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 text-left hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-lg bg-emerald-50 mr-4">
                    <Users className="w-8 h-8 text-emerald-500" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Enterprise Plan</h3>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    "Dedicated account management",
                    "Custom compliance settings",
                    "Advanced reporting & analytics",
                    "Custom integration & API access",
                    "Enterprise-grade security & SSO"
                  ].map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <Check className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact">
                  <Button className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white">
                    Schedule Consultation
                  </Button>
                </Link>
              </div>
              
              <div className="bg-gradient-to-br from-sky-600 to-sky-700 p-8 rounded-xl shadow-lg text-left text-white hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-lg bg-white/20 mr-4">
                    <Trophy className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold">Premier Partnership</h3>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    "All Enterprise Plan features",
                    "White-labeled talent portal",
                    "Priority talent matching",
                    "Executive dashboard access",
                    "Strategic talent planning services"
                  ].map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <Check className="h-5 w-5 text-sky-300 mr-2 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact">
                  <Button variant="outline" className="w-full border-white hover:bg-white/10 text-white">
                    Contact Sales
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="flex justify-center">
              <p className="max-w-2xl text-sm text-gray-500">
                All enterprise plans include 24/7 support, SLA guarantees, and custom contract terms. Contact our sales team for detailed pricing and customized solutions for your organization.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Enterprise;
